import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type ImportJob, type ImportReport } from '../api'
import { Icon } from './Icon'
import { counted } from '../plural'

/**
 * Import a documentation site, from inside the product.
 *
 * The four importers have existed for weeks and were reachable only from a
 * terminal with a checkout of this repository — which is exactly the audience
 * that does not need them. This screen is the door.
 *
 * The REPORT is most of the interface, on purpose. An import that says
 * "success" and quietly lost the callouts is an importer nobody trusts twice;
 * these importers earn trust by itemising what needs a human and what could not
 * come, so this screen gives the report the space a result deserves.
 *
 * What is on screen is read from the SERVER, not held here. Converting a real
 * documentation site takes minutes, and the person doing it will switch tabs,
 * reload, or close the laptop. Before this, all three lost the import and left
 * no way to find out how far it had got; now the newest job is fetched on
 * arrival, so coming back shows the same import at the same place — including
 * one that failed, with the reason and the door back in.
 */
/**
 * Every platform the importer reads, offered here.
 *
 * This listed four while the service read eight: Nextra, Notion, Confluence and
 * a folder of HTML were all supported, tested, and unreachable from the screen
 * — somebody with a Confluence export had to trust "detect from the archive"
 * and hope. Detection is still first and still the right default; naming them
 * is how a person knows their export is one this product understands.
 */
const PLATFORMS = [
  { id: '', label: 'Detect from the archive' },
  { id: 'gitbook', label: 'GitBook' },
  { id: 'docusaurus', label: 'Docusaurus' },
  { id: 'mintlify', label: 'Mintlify' },
  { id: 'nextra', label: 'Nextra' },
  { id: 'notion', label: 'Notion' },
  { id: 'confluence', label: 'Confluence (HTML space export)' },
  { id: 'html', label: 'A folder of HTML' },
  { id: 'markdown', label: 'Markdown folder (MkDocs, VitePress, Docsify, mdBook)' },
] as const

const NAMES: Record<ImportReport['platform'], string> = {
  gitbook: 'GitBook',
  docusaurus: 'Docusaurus',
  mintlify: 'Mintlify',
  nextra: 'Nextra',
  notion: 'Notion',
  confluence: 'Confluence',
  html: 'a folder of HTML',
  markdown: 'a Markdown folder',
}

const inFlight = (job: ImportJob) => job.status === 'queued' || job.status === 'running'

/**
 * Which documentation an import is aimed at, in the words a person chose.
 *
 * The screens said "everything lands on a draft, so your published site does
 * not change" whatever the answer — true of the live site and beside the point
 * for somebody who deliberately sent an import into `v1`, who has no way of
 * telling from the screen that it went where they asked.
 */
const aimedAt = (job: ImportJob, versions: { name: string; label: string }[]): string | null => {
  if (!job.targetRef) return null
  const name = job.targetRef.replace(/^version\//, '')
  return versions.find((v) => v.name === name)?.label ?? name
}

export function ImportScreen({
  onOpenDraft,
}: {
  onOpenDraft: (branch: string) => Promise<void> | void
}) {
  const [opening, setOpening] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [platform, setPlatform] = useState('')
  /**
   * Which documentation to import INTO.
   *
   * Empty is the live site, which is what an import has always meant. The
   * control appears only for a project that has versions, because for everybody
   * else it is a field with one choice.
   */
  const [version, setVersion] = useState('')
  const [versions, setVersions] = useState<{ name: string; label: string }[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [job, setJob] = useState<ImportJob | null>(null)
  const [dismissed, setDismissed] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    try {
      const { jobs } = await api.importJobs()
      setJob(jobs[0] ?? null)
    } catch {
      // A failed poll is not worth a message: the next one is two seconds away,
      // and an error banner over a working import would be the wrong story.
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void api
      .versions()
      .then((found) => setVersions(found.versions))
      // A project with no versions is the ordinary case, and a failed read here
      // must not stop somebody importing.
      .catch(() => setVersions([]))
  }, [])

  /**
   * Keep looking while an import is in flight, and only then.
   *
   * A screen that polls for ever costs a request every two seconds for as long
   * as somebody leaves the tab open, and once the job has settled there is
   * nothing left to change. Two seconds rather than the four the publish
   * history uses, because this one has a bar somebody is watching move.
   */
  const watching = job !== null && inFlight(job)
  useEffect(() => {
    if (!watching) return
    const timer = window.setInterval(() => void load(), 2000)
    return () => window.clearInterval(timer)
  }, [watching, load])

  const run = useCallback(async () => {
    if (!file || busy) return
    setBusy(true)
    setError(null)
    try {
      const started = await api.startImport(file, platform || undefined, version || undefined)
      setDismissed(null)
      setJob(started)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }, [file, platform, version, busy])

  const retry = useCallback(async () => {
    if (!job || busy) return
    setBusy(true)
    setError(null)
    try {
      const { job: back } = await api.retryImport(job.id)
      setJob(back)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }, [job, busy])

  const startAgain = () => {
    if (job) setDismissed(job.id)
    setFile(null)
    setError(null)
  }

  const showing = job && job.id !== dismissed ? job : null

  if (showing && inFlight(showing)) {
    return <Running job={showing} into={aimedAt(showing, versions)} />
  }

  if (showing?.status === 'failed') {
    return (
      <Failed
        job={showing}
        busy={busy}
        error={error}
        onRetry={() => void retry()}
        onDismiss={startAgain}
      />
    )
  }

  if (showing?.status === 'done' && showing.report) {
    const report = showing.report
    const human = report.notes.filter((n) => n.kind !== 'derived')
    const toCheck = human.reduce((n, x) => n + x.count, 0)
    return (
      <div className="screen">
        <header className="screen__head">
          <h1>Imported from {NAMES[report.platform]}</h1>
          <p className="screen__sub">
            {aimedAt(showing, versions)
              ? `Everything landed on a draft aimed at ${aimedAt(showing, versions)} — that version has not changed yet.`
              : 'Everything landed on a draft — your published site has not changed.'}{' '}
            Read what needs a person, then open the draft to review and publish it.
          </p>
        </header>

        <div className="stats">
          <div className="stat">
            <b>{report.pages}</b>
            <span>{counted(report.pages, 'page', 'pages')}</span>
          </div>
          <div className="stat">
            <b>{report.images}</b>
            <span>{counted(report.images, 'image carried', 'images carried')}</span>
          </div>
          {report.snippets > 0 && (
            <div className="stat">
              <b>{report.snippets}</b>
              <span>{counted(report.snippets, 'shared block', 'shared blocks')}</span>
            </div>
          )}
          {/*
            The old addresses the source site was already keeping alive. Shown
            because their absence is invisible: a page that arrives without the
            redirect that used to reach it looks like a successful import until
            somebody follows a link from a blog post nobody can edit.
          */}
          {report.redirects.carried > 0 && (
            <div className="stat" data-redirects-carried>
              <b>{report.redirects.carried}</b>
              <span>
                {report.redirects.beside
                  ? counted(report.redirects.carried, 'redirect proposed', 'redirects proposed')
                  : counted(report.redirects.carried, 'redirect carried', 'redirects carried')}
              </span>
            </div>
          )}
          <div className="stat">
            <b>{report.skipped.length}</b>
            <span>already existed, kept</span>
          </div>
          <div className="stat">
            <b>{toCheck}</b>
            <span>{counted(toCheck, 'thing to check', 'things to check')}</span>
          </div>
        </div>

        {report.unresolvedImages > 0 && (
          <p className="screen__warn screen__warn--problem">
            <Icon name="alert" size={13} /> {report.unresolvedImages} image reference
            {report.unresolvedImages === 1 ? '' : 's'} the archive did not contain still point at
            the old site. A published-site scrape cannot carry them; a Git export can.
          </p>
        )}
        {report.rejectedFiles.length > 0 && (
          <p className="screen__warn screen__warn--problem">
            <Icon name="alert" size={13} /> {report.rejectedFiles.length} file
            {report.rejectedFiles.length === 1 ? '' : 's'} the asset store refused:{' '}
            {report.rejectedFiles
              .slice(0, 3)
              .map((f) => f.path)
              .join(', ')}
          </p>
        )}

        {human.length > 0 && (
          <div className="notes">
            <h2>What needs a human</h2>
            {human.map((note) => (
              <div key={note.rule} className="note-row">
                <span className={`note-row__count note-row__count--${note.kind}`}>
                  {note.count}×
                </span>
                <div>
                  <b>{note.rule.replace(/-/g, ' ')}</b>
                  <p>{note.message}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="screen__actions">
          <button
            className="btn btn--primary"
            disabled={opening}
            onClick={async () => {
              setOpening(true)
              try {
                await onOpenDraft(report.branch)
              } finally {
                setOpening(false)
              }
            }}
          >
            {opening ? (
              <>
                <Icon name="spinner" size={13} /> Opening…
              </>
            ) : (
              'Open the draft'
            )}
          </button>
          <button className="btn btn--ghost" onClick={startAgain}>
            Import another
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="screen">
      <header className="screen__head">
        <h1>Import documentation</h1>
        <p className="screen__sub">
          Bring a site from GitBook, Docusaurus, Mintlify, or any folder of Markdown. Everything
          lands on a draft for review — your published site does not change until you publish.
        </p>
      </header>

      <div
        className={`dropzone${dragging ? ' dropzone--over' : ''}${file ? ' dropzone--filled' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          const dropped = e.dataTransfer.files?.[0]
          if (dropped) setFile(dropped)
        }}
        onClick={() => input.current?.click()}
        role="button"
        tabIndex={0}
        aria-label="Choose a zip to import, or drop one here"
        /**
         * Enter AND Space.
         *
         * A real `<button>` answers both, and anything wearing `role="button"`
         * has promised to behave like one — so this was reachable by keyboard,
         * announced as a button, and silent on the key half of the people who
         * would press it use. Space also scrolls the page by default while
         * focus is on it, which is the second half of the same bug.
         */
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return
          e.preventDefault()
          input.current?.click()
        }}
      >
        <input
          ref={input}
          type="file"
          accept=".zip,application/zip"
          hidden
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Icon name={file ? 'check' : 'plus'} size={18} />
        {file ? (
          <p>
            <b>{file.name}</b> — {(file.size / 1024 / 1024).toFixed(1)} MB
          </p>
        ) : (
          <p>
            Drop the export here, or <b>choose a zip</b>
          </p>
        )}
      </div>

      <label className="screen__field">
        <span>Platform</span>
        <select className="select" value={platform} onChange={(e) => setPlatform(e.target.value)}>
          {PLATFORMS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>

      {versions.length > 0 && (
        <label className="screen__field">
          <span>Into</span>
          <select className="select" value={version} onChange={(e) => setVersion(e.target.value)}>
            <option value="">The live documentation</option>
            {versions.map((v) => (
              <option key={v.name} value={v.name}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}

      <div className="screen__actions">
        <button className="btn btn--primary" disabled={!file || busy} onClick={() => void run()}>
          {busy ? (
            <>
              <Icon name="spinner" size={13} /> Sending…
            </>
          ) : (
            'Import to a draft'
          )}
        </button>
      </div>
    </div>
  )
}

/**
 * An import that is happening.
 *
 * Two numbers and a name, because those are the three questions somebody
 * watching actually has: how much is left, is it moving, and is it stuck on
 * something. A bar with no count answers none of them, and a spinner answers
 * fewer.
 *
 * There is deliberately no cancel here. The work is on a draft nobody has
 * published, so the cost of letting it finish is a branch somebody deletes.
 */
function Running({ job, into }: { job: ImportJob; into: string | null }) {
  const total = job.pagesTotal
  const done = job.pagesDone
  // Only once the archive has been walked. Before that the total is unknown,
  // and a bar that guesses is a bar that jumps backwards.
  const percent = total && total > 0 ? Math.min(100, Math.round((done / total) * 100)) : null

  return (
    <div className="screen">
      <header className="screen__head">
        <h1>Importing {job.archiveName}</h1>
        <p className="screen__sub">
          This keeps going if you close the tab — come back to this screen for the state of it.
          {into
            ? ` Everything lands on a draft aimed at ${into}, so nothing readers can see changes yet.`
            : ' Everything lands on a draft, so your published site does not change.'}
        </p>
      </header>

      <div className="importing">
        <div className="importing__line">
          <span className="importing__count">
            {percent === null
              ? 'Unpacking the archive…'
              : `${done} of ${total} page${total === 1 ? '' : 's'}`}
          </span>
          {percent !== null && <span className="importing__pct">{percent}%</span>}
        </div>
        <div
          className="progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total ?? 100}
          {...(percent === null ? {} : { 'aria-valuenow': done })}
          aria-label="Pages imported"
        >
          <div className="progress__fill" style={{ width: `${percent ?? 0}%` }} />
        </div>
        <p className="importing__now">
          <Icon name="spinner" size={13} />{' '}
          {job.status === 'queued'
            ? 'Waiting for a worker to pick this up.'
            : job.currentFile
              ? `Converting ${job.currentFile}`
              : 'Finishing up.'}
        </p>
        {job.attempts > 1 && (
          <p className="importing__retrying">
            Attempt {job.attempts}. It picked up where the last one stopped
            {job.error ? `, after: ${job.error}` : '.'}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * An import that gave up.
 *
 * The reason first, in the words the server wrote for a person, and the
 * technical text folded away beside it rather than instead of it — somebody has
 * to be able to paste that into an issue without it being the first thing a
 * writer reads.
 *
 * Retry rather than "start again" because it resumes: whatever already landed
 * on the draft stays there, so a job that stopped near the end is a short
 * second run and not a repeat of the whole migration.
 */
function Failed({
  job,
  busy,
  error,
  onRetry,
  onDismiss,
}: {
  job: ImportJob
  busy: boolean
  error: string | null
  onRetry: () => void
  onDismiss: () => void
}) {
  return (
    <div className="screen">
      <header className="screen__head">
        <h1>The import stopped</h1>
        <p className="screen__sub">
          {job.archiveName}
          {job.pagesTotal ? ` — ${job.pagesDone} of ${job.pagesTotal} pages had been imported` : ''}
          . Nothing was published, and nothing that already landed on the draft was lost.
        </p>
      </header>

      <div className="failure">
        <p className="failure__why">
          <Icon name="alert" size={14} /> {job.error ?? 'It stopped without saying why.'}
        </p>
        {job.errorDetail && (
          <details className="failure__detail">
            <summary>Technical detail</summary>
            <pre>{job.errorDetail}</pre>
          </details>
        )}
      </div>

      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}

      <div className="screen__actions">
        <button className="btn btn--primary" disabled={busy} onClick={onRetry}>
          {busy ? (
            <>
              <Icon name="spinner" size={13} /> Queueing…
            </>
          ) : (
            'Retry from where it stopped'
          )}
        </button>
        <button className="btn btn--ghost" onClick={onDismiss}>
          Import something else
        </button>
      </div>
    </div>
  )
}
